import { useState } from "react";
import { Radar, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtPct01, fmtScore, identityLabel, narrativeDetailKey, tokenDetailKey } from "@/lib/hypeV2Format";
import HypeDetailDrawer from "@/components/hype/HypeDetailDrawer";

export default function HypeRadarV2Section({
  v2,
  v2Ecosystem,
  onEcosystemChange,
  onRefresh,
}) {
  const [drawer, setDrawer] = useState({ open: false, kind: null, id: "" });

  const openNarrative = (n) => {
    const id = narrativeDetailKey(n);
    if (!id) return;
    setDrawer({ open: true, kind: "narrative", id });
  };
  const openToken = (t) => {
    const id = tokenDetailKey(t);
    if (!id) return;
    setDrawer({ open: true, kind: "token", id });
  };

  return (
    <section data-testid="hype-v2-panel" className="space-y-3">
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <Radar className="h-4 w-4 text-gold" />
        <span className="text-sm font-bold">Hype Radar V2</span>
        <span className="text-[11px] text-zinc-600">
          Click a row for narrative or token detail. Experimental evidence only.
        </span>
        <Select value={v2Ecosystem} onValueChange={onEcosystemChange}>
          <SelectTrigger data-testid="hype-v2-ecosystem" className="ml-auto h-8 w-[130px] border-white/15 bg-black text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="base">Base</SelectItem>
            <SelectItem value="robinhood">Robinhood</SelectItem>
            <SelectItem value="solana">Solana</SelectItem>
          </SelectContent>
        </Select>
        <button type="button" data-testid="hype-v2-refresh" onClick={onRefresh}
          className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs hover:bg-white/5">
          <RefreshCw className="h-3 w-3" /> Refresh V2
        </button>
      </div>
      <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-[11px] text-amber-100/80" data-testid="hype-v2-disclaimer">
        External intelligence = contextual / experimental Narrative Intelligence.
        Not primary Backing rank. Not a return prediction.
        Hype Score weights remain 35 / 30 / 20 / 15.
        Provider names below are <span className="text-amber-200/90">internal provenance only</span>.
        {v2 && !v2.hype_intelligence_enabled && (
          <span className="ml-2 font-mono text-zinc-500">HYPE_INTELLIGENCE_ENABLED=false</span>
        )}
      </div>
      {v2?.hype_intelligence_enabled && (
        <>
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#080808]" data-testid="hype-v2-tokens">
            <div className="border-b border-white/10 px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-zinc-600">
              Ecosystem tokens · score + EI evidence · click for detail
            </div>
            {(v2.tokens || []).slice(0, 12).map((t) => {
              const ei = t.external_intelligence || {};
              return (
                <button
                  type="button"
                  key={`${t.chain}-${t.contract_address || t.mint_address || t.symbol}`}
                  data-testid={`hype-v2-token-row-${t.entity_id || t.symbol || "x"}`}
                  onClick={() => openToken(t)}
                  className="flex w-full cursor-pointer flex-wrap items-center gap-3 border-b border-white/5 px-4 py-2.5 text-left text-xs last:border-0 hover:bg-white/[0.04]"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-zinc-200">{t.symbol || t.name || "—"}</div>
                    <div className="truncate font-mono text-[10px] text-zinc-600">{t.contract_address || t.mint_address || "—"}</div>
                  </div>
                  <div className="font-mono text-gold">{fmtScore(t.hype_score)}</div>
                  <div className="font-mono text-[10px] text-zinc-500">
                    mv {fmtPct01(t.components?.market_velocity)} ·
                    bv {fmtPct01(t.components?.backing_velocity)} ·
                    ca {fmtPct01(t.components?.caller_activity)} ·
                    fr {fmtPct01(t.components?.freshness)}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {(ei.providers || []).map((p) => (
                      <span key={p} title="Internal provenance"
                        className="rounded border border-white/15 px-1.5 py-0.5 font-mono text-[9px] uppercase text-zinc-400">{p}</span>
                    ))}
                    <span className="font-mono text-[10px] text-zinc-500">ei:{ei.evidence_count ?? 0}</span>
                    {ei.identity_status && (
                      <span className="rounded border border-white/10 px-1.5 py-0.5 font-mono text-[9px] text-zinc-400">
                        {identityLabel(ei.identity_status)}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
            {(v2.tokens || []).length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-zinc-600">No ecosystem tokens scored</div>
            )}
          </div>
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#080808]" data-testid="hype-v2-narratives">
            <div className="border-b border-white/10 px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-zinc-600">
              Narrative Radar · click for detail
            </div>
            {(v2.narrative_radar || []).slice(0, 12).map((n) => (
              <button
                type="button"
                key={n.canonical_narrative_key || n.title}
                data-testid={`hype-v2-narrative-row-${n.canonical_narrative_key || "x"}`}
                onClick={() => openNarrative(n)}
                className="flex w-full cursor-pointer flex-wrap items-center gap-3 border-b border-white/5 px-4 py-2.5 text-left text-xs last:border-0 hover:bg-white/[0.04]"
              >
                <div className="min-w-0 flex-1 font-medium text-zinc-200">{n.title || n.canonical_narrative_key || "—"}</div>
                <span className="rounded border border-white/10 px-1.5 py-0.5 font-mono text-[9px] text-zinc-400">
                  {identityLabel(n.status)}
                </span>
                <span className="font-mono text-[10px] text-zinc-500">mom {fmtPct01(n.momentum)}</span>
                <span className="font-mono text-[10px] text-zinc-500">cons {fmtPct01(n.consensus_score)}</span>
                {(n.providers || []).map((p) => (
                  <span key={p} className="rounded border border-white/15 px-1.5 py-0.5 font-mono text-[9px] uppercase text-zinc-400">{p}</span>
                ))}
              </button>
            ))}
            {(v2.narrative_radar || []).length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-zinc-600">No narratives in local intelligence store</div>
            )}
          </div>
        </>
      )}

      <HypeDetailDrawer
        open={drawer.open}
        kind={drawer.kind}
        id={drawer.id}
        audience="internal"
        onOpenChange={(open) => setDrawer((d) => ({ ...d, open }))}
      />
    </section>
  );
}
